import {
  Button,
  DropdownButton,
  FlexBox,
  IconButton,
  List,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useCopyToClipboard } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { getChannelName } from 'app/utils/channelName';
import ConversationSummaryHelper from 'app/utils/conversationSummaryHelper';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { ConversationChannelIcon } from 'components/conversation/conversationChannelIcon';
import { useCommandHandler } from 'utils/commands';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import styles from './conversationHeadline.module.scss';

const copyToClipboardTooltip = 'Click to copy ID';
const copiedToClipboardTooltip = 'ID copied';
const failedToCopyClipboardTooltip = 'Failed to copy ID';

type Props = {
  isFocusMode?: boolean;
};

export const ConversationHeadline = ({
  isFocusMode = false,
}: Props): JSX.Element => {
  const [tooltipLabel, setTooltipLabel] = useState(copyToClipboardTooltip);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const subjectRef = useRef<HTMLDivElement>(null);
  const [metaConversation, requester, conversation] = useAppSelector(
    (state) => [
      state.conversation.metaConversation,
      state.conversation.metaConversation.requester,
      state.conversation.metaConversation.conversation,
    ],
    shallowEqual,
  );

  const [{ error: copyToClipboardError }, copyToClipboard] =
    useCopyToClipboard();

  const conversationSummaryHelper = new ConversationSummaryHelper(
    metaConversation,
  );
  const convSummarySubject = conversationSummaryHelper.getSubject();

  const [updateSubject] =
    conversationsApi.useUpdateConversationSubjectMutation();
  const handleCopyToClipboardClick = useCallback(() => {
    copyToClipboard(metaConversation.conversation.id?.toString() || '');

    if (copyToClipboardError) {
      setTooltipLabel(failedToCopyClipboardTooltip);
    } else {
      setTooltipLabel(copiedToClipboardTooltip);
    }

    const tooltipTimeout = setTimeout(() => {
      setTooltipLabel(copyToClipboardTooltip);
      clearTimeout(tooltipTimeout);
    }, 2000);
  }, [
    copyToClipboard,
    setTooltipLabel,
    copyToClipboardError,
    metaConversation,
  ]);

  const handleEditSubjectClick = useCallback(() => {
    setIsEditingSubject(true);
  }, []);

  useCommandHandler({
    name: 'conversation:editSubject',
    actionFn: () => {
      if (!isFocusMode && !isEditingSubject) {
        setIsEditingSubject(true);
      }
    },
  });

  useEffect(() => {
    if (isEditingSubject && subjectRef.current) {
      // Use setTimeout to ensure focus happens after any modal closing animations
      // and after modal's restoreFocus behavior completes
      const timeoutId = setTimeout(() => {
        if (subjectRef.current) {
          subjectRef.current.focus();
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(subjectRef.current);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [isEditingSubject]);

  const handleSaveSubject = useCallback(async () => {
    const newSubject = subjectRef.current?.textContent?.trim() || '';
    if (newSubject && newSubject !== convSummarySubject) {
      await updateSubject({
        csid: metaConversation.conversation.id || 0,
        subject: newSubject,
      });
    }
    setIsEditingSubject(false);
  }, [convSummarySubject, updateSubject, metaConversation]);

  const handleCancelEdit = useCallback(() => {
    if (subjectRef.current) {
      subjectRef.current.textContent = convSummarySubject || '';
    }
    setIsEditingSubject(false);
  }, [convSummarySubject]);

  const handleSubjectKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveSubject();
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    },
    [handleSaveSubject, handleCancelEdit],
  );

  const handleDropdownToggle = useCallback(
    (isOpen) =>
      isOpen && ampli.conversationHeaderConversationDetailsMenuOpened(),
    [],
  );

  const userName = requester && getUserDisplayName(requester);
  const subject =
    isFocusMode && convSummarySubject ? convSummarySubject : userName;
  const title = conversation.is_abandoned ? `Abandoned: ${subject}` : subject;
  const info = conversationSummaryHelper.getInfoListLines();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  useCommandHandler({
    name: 'conversation:details',
    actionFn: () => {
      setIsDetailsOpen((prev) => !prev);
    },
  });

  const detailsTooltipContent = (
    <CommandKeybindingsTooltipContent commandName="conversation:details">
      Conversation details
    </CommandKeybindingsTooltipContent>
  );

  return (
    <>
      <FlexBox alignItems="center" gap="200">
        {conversation.initial_channel && (
          <Tooltip
            wrap="div"
            wrapProps={{ className: styles.iconTooltipWrapper }}
            content={getChannelName(conversation.initial_channel)}
          >
            <ConversationChannelIcon
              channel={conversation.initial_channel}
              color="secondary"
            />
          </Tooltip>
        )}

        {requester && <Text.Heading variant="heading-1">{title}</Text.Heading>}

        <FlexBox alignItems="center">
          <Tooltip
            content={tooltipLabel}
            wrapProps={{
              // A small hack to stop the tooltip hiding on click
              onMouseDown: (e) => e.preventDefault(),
              onClick: (e) => e.preventDefault(),
            }}
          >
            <Button
              variant="tertiary"
              className={styles.csid}
              onClick={handleCopyToClipboardClick}
              styleText={false}
            >
              #{metaConversation.conversation.id}
            </Button>
          </Tooltip>

          <Tooltip content={detailsTooltipContent}>
            <DropdownButton
              show={isDetailsOpen}
              icon="angle-down"
              buttonProps={{
                variant: 'tertiary',
              }}
              onToggle={handleDropdownToggle}
              fixed={true}
              dropdownProps={{
                className: styles.dropdown,
              }}
              dropdown={
                <List>
                  {info.map(({ key, value }) => (
                    <List.Item
                      key={key}
                      className={styles.infoItem}
                      withHoverEffect={false}
                      contentLeft={
                        <Text.Paragraph className={styles.infoItemLeft}>
                          {key}
                        </Text.Paragraph>
                      }
                      contentRight={
                        <Text.Paragraph className={styles.infoItemRight}>
                          {value}
                        </Text.Paragraph>
                      }
                    />
                  ))}
                </List>
              }
            />
          </Tooltip>
        </FlexBox>
      </FlexBox>
      {!isFocusMode && !isEditingSubject && (
        <div className={styles.subjectContainer}>
          <Text.Paragraph>{convSummarySubject}</Text.Paragraph>
          <Tooltip
            content={
              <CommandKeybindingsTooltipContent commandName="conversation:editSubject">
                Edit subject
              </CommandKeybindingsTooltipContent>
            }
          >
            <IconButton
              icon="pen"
              variant="tertiary"
              onClick={handleEditSubjectClick}
              className={styles.editIcon}
            />
          </Tooltip>
        </div>
      )}
      {!isFocusMode && isEditingSubject && (
        <div className={styles.subjectContainer}>
          <div
            ref={subjectRef}
            contentEditable
            suppressContentEditableWarning
            spellCheck={false}
            onBlur={handleSaveSubject}
            onKeyDown={handleSubjectKeyDown}
            className={styles.subjectEditable}
          >
            {convSummarySubject}
          </div>
        </div>
      )}
    </>
  );
};
